import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography, Alert, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { loginWithMicrosoft } from '../utils/api';
import {
  acquireMicrosoftIdToken,
  clearMicrosoftRedirectStarted,
  handleMicrosoftRedirect,
} from '../utils/msalClient';

const isSsoDebugEnabled = process.env.NODE_ENV === 'development';

const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [status, setStatus] = useState('Reading Microsoft sign-in response...');
  const [details, setDetails] = useState([]);
  const hasHandledRedirect = useRef(false);

  const addDetail = (message, data = {}) => {
    if (!isSsoDebugEnabled) {
      return;
    }

    const nextDetail = {
      message,
      data,
      at: new Date().toLocaleTimeString(),
    };
    setDetails((current) => [...current, nextDetail]);
    console.warn(`[MSAL] ${message}`, data);
  };

  useEffect(() => {
    if (hasHandledRedirect.current) {
      return undefined;
    }

    hasHandledRedirect.current = true;
    let redirectTimer;

    const completeMicrosoftLogin = async () => {
      try {
        addDetail('Callback page mounted', {
          currentUrl: window.location.href,
          hasHash: Boolean(window.location.hash),
          hasSearch: Boolean(window.location.search),
        });

        setStatus('Completing Microsoft redirect...');
        const msalResponse = await handleMicrosoftRedirect();
        addDetail('Redirect response handled', {
          hasResponse: Boolean(msalResponse),
          hasIdToken: Boolean(msalResponse?.idToken),
          accountUsername: msalResponse?.account?.username,
        });

        setStatus('Getting Microsoft identity token...');
        const idToken = msalResponse?.idToken || await acquireMicrosoftIdToken();

        if (!idToken) {
          throw new Error('Microsoft did not return a sign-in response.');
        }

        setStatus('Signing in to Selectivate...');
        addDetail('Posting ID token to backend');
        const response = await loginWithMicrosoft(idToken);
        addDetail('Backend Microsoft login response', {
          success: response.success,
          message: response.message,
          userEmail: response.user?.email,
        });

        if (!response.success) {
          throw new Error(response.message || 'Microsoft login failed.');
        }

        clearMicrosoftRedirectStarted();
        localStorage.setItem('currentUser', JSON.stringify(response.user));
        window.location.replace('/home');
      } catch (err) {
        const message = err.message || 'Microsoft login failed. Please try again.';
        addDetail('Callback failed', { message });
        console.error('[MSAL] Callback failed', err);
        clearMicrosoftRedirectStarted();
        sessionStorage.setItem('microsoftSsoError', message);
        setError(message);
        redirectTimer = setTimeout(() => {
          navigate(`/login?sso_error=${encodeURIComponent(message)}`, { replace: true });
        }, 6000);
      }
    };

    completeMicrosoftLogin();

    return () => {
      if (redirectTimer) {
        clearTimeout(redirectTimer);
      }
    };
  }, [navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        px: 3,
        textAlign: 'center',
      }}
    >
      <Paper
        elevation={4}
        sx={{
          width: '100%',
          maxWidth: 640,
          p: 3,
          bgcolor: '#1e1e1e',
          border: '1px solid rgba(255, 255, 255, 0.12)',
        }}
      >
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography variant="h6">{status}</Typography>
          </Box>
        )}

        {isSsoDebugEnabled && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Microsoft sign-in diagnostics
            </Typography>
            {details.map((detail) => (
              <Box
                key={`${detail.at}-${detail.message}`}
                sx={{
                  textAlign: 'left',
                  fontFamily: 'monospace',
                  fontSize: 12,
                  color: '#d0d0d0',
                  borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                  py: 1,
                  overflowWrap: 'anywhere',
                }}
              >
                {detail.at} - {detail.message}
                {Object.keys(detail.data).length > 0 && (
                  <Box component="pre" sx={{ m: 0, mt: 0.5, whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(detail.data, null, 2)}
                  </Box>
                )}
              </Box>
            ))}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default AuthCallbackPage;
